import { getUsersByCompany2 } from 'API/company';
import { Roles } from 'enums';
import { useUserData } from 'hooks/use-user-data';
import _ from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import useErrorStore from 'stores/error-store';
import { CompanySearchTypes, TenantScopes, User } from '../account-management.types';

const useTenantUsers = (
  tenantId: string | null,
  scope: TenantScopes = TenantScopes.USERS,
  searchType: CompanySearchTypes = CompanySearchTypes.USER,
  searchParam: string = ''
) => {
  const { role } = useUserData();
  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);

  const itemsPerPage = 10;

  // Create search regex pattern for better matching
  const searchPattern = useMemo(() => {
    if (!searchParam.trim()) return null;

    const escapedTerms = searchParam
      .trim()
      .split(/\s+/)
      .map((term) => _.escapeRegExp(term))
      .join('|');

    return new RegExp(escapedTerms, 'i');
  }, [searchParam]);

  // Filter users based on search parameters using lodash and regex
  const filteredUsers = useMemo(() => {
    if (searchType !== CompanySearchTypes.USER || !searchPattern || _.isEmpty(users)) {
      return users;
    }

    const filtered = _.filter(users, (user) => {
      const searchableText = _.compact([user.firstname, user.lastname, user.email]).join(' ');

      return searchPattern.test(searchableText);
    });

    // If filtered results are empty, return all users unfiltered
    return _.isEmpty(filtered) ? users : filtered;
  }, [users, searchType, searchPattern]);

  // Paginate filtered results
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return _.slice(filteredUsers, startIndex, endIndex);
  }, [filteredUsers, page, itemsPerPage]);

  const abortController = useRef<AbortController | null>(null);

  const resetAbortController = () => {
    if (abortController.current) {
      abortController.current.abort();
      abortController.current = null;
    }
  };

  const fetchTenantUsers = async (newPage: number = page) => {
    if (!tenantId) return;

    setLoading(true);
    resetAbortController();

    abortController.current = new AbortController();
    const res = await getUsersByCompany2(tenantId, role === Roles.ADMIN || role === Roles.OWNER, abortController.current.signal);
    await new Promise((resolve) => setTimeout(resolve, 500));

    if (!res.error) {
      setUsers(res.users);
      setPage(newPage);
    } else {
      resetTenantUsers();
      handleOpenErrorDialog(res?.error, `/company/${tenantId}/users`, 'get');
    }
    setLoading(false);
  };

  const resetTenantUsers = () => {
    setUsers([]);
    setPage(1);
  };

  // Fetch users when tenant or scope changes
  useEffect(() => {
    if (role === Roles.ADMIN || role === Roles.AGENCY || role === Roles.OWNER) {
      if (tenantId && (scope === TenantScopes.USERS || scope === TenantScopes.LICENSES)) {
        fetchTenantUsers(1);
      } else {
        resetTenantUsers();
      }
    }

    return () => {
      resetAbortController();
      resetTenantUsers();
    };
  }, [tenantId, scope]);

  // Reset to first page when search parameters change
  useEffect(() => {
    if (searchType === CompanySearchTypes.USER && scope === TenantScopes.USERS) {
      setPage(1);
    }
  }, [searchType, scope, searchParam]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return {
    tenantUsers: paginatedUsers,
    total: filteredUsers.length,
    loading,
    itemsPerPage,
    page,
    handleChangePage,
    fetchTenantUsers,
    resetTenantUsers,
  };
};

export default useTenantUsers;
